import {
  ROUND_TITLE_BY_ROUND_TYPE,
  TED_WORD_SETTINGS,
} from "app/data/game-settings";
import type { WordData } from "app/game-data/useFetchGameData";
import { RoundType } from "app/game-data/useFetchGameData";
import { ScoreAction } from "app/score/scoreState";
import { useScoreDispatch } from "app/score/useScore";
import Caption from "app/ted-word/Caption";
import ClipLengthButton from "app/ted-word/ClipLengthButton";
import { TedWordProvider } from "app/ted-word/TedWordProvider";
import { TedWordAction } from "app/ted-word/tedWordState";
import {
  useTedWordActionDispatch,
  useTedWordState,
} from "app/ted-word/useTedWord";
import NextRoundButton from "app/ui/NextRoundButton";
import RoundIntroScreen from "app/ui/RoundIntroScreen";
import VideoEmbed from "app/ui/VideoEmbed";
import WordSpellingRecord from "app/ui/WordSpellingRecord";
import cn from "app/utils/classnames";
import { useState } from "react";
import useWindowSize, { WindowSize } from "app/utils/useWindowSize";
import mixpanel from "mixpanel-browser";
import useGameData from "app/game-data/useGameData";
import { useFetchTedTalkMetadata } from "app/utils/useFetchTedTalkMetadata";
import { useGameState } from "app/game/useGame";
import KeyboardGroup from "app/spelling-input/KeyboardGroup";
import Header from "app/ui/Header";

interface TedWordRoundProps {
  word: WordData;
  sentence: string;
  roundIndex: number;
  videoSlug: string;
  videoStartTime: number;
  videoEndTime: number;
  videoWordTime: number;
  onComplete: () => void;
  className?: string;
}

function TedWordRound({
  word,
  sentence,
  roundIndex,
  onComplete,
  className,
  videoSlug,
  videoStartTime,
  videoEndTime,
  videoWordTime,
}: TedWordRoundProps) {
  const gameData = useGameData();
  const roundState = useTedWordState();
  const { inputType } = useGameState();
  const roundDispatch = useTedWordActionDispatch();
  const scoreDispatch = useScoreDispatch();
  const [jumpToWord, setJumpToWord] = useState(false);
  const { windowHeight } = useWindowSize(1024, 750);
  const { longTitle: videoTitle } = useFetchTedTalkMetadata(videoSlug);

  // The player doesn't need the ability to jump to the word if the word's
  // timestamp is the same as the video start timestamp - so we hide the buttons.
  const hideClipLengthButtons = videoWordTime === videoStartTime;

  const roundComplete =
    roundState.userSpellings.length >= TED_WORD_SETTINGS.maxAttempts ||
    roundState.answeredCorrectly;

  function handleKeyboardGroupSubmit(input: string) {
    const userSpelling = input.toLowerCase();
    const correctSpelling = word.wordText.toLowerCase();
    const isCorrect = userSpelling === correctSpelling;
    const points = isCorrect ? getWordScore(roundState.numWrongAnswers) : 0;

    roundDispatch({
      type: TedWordAction.SUBMITTED_SPELLING,
      payload: {
        spelling: {
          userSpelling: userSpelling,
          correctSpelling: correctSpelling,
          pointValue: points,
        },
      },
    });

    let sendSpelling = false;
    if (isCorrect) {
      sendSpelling = true;
      scoreDispatch({
        type: ScoreAction.POINTS_EARNED,
        payload: { roundIndex: roundIndex, points: points },
      });
    }

    // If the player ran out of attempts, we still need to dispatch the
    // WORD_SPELLED event so that the spelling gets stored and we can display
    // it on the game over screen.
    sendSpelling =
      sendSpelling ||
      roundState.userSpellings.length >= TED_WORD_SETTINGS.maxAttempts - 1;

    if (sendSpelling) {
      scoreDispatch({
        type: ScoreAction.WORD_SPELLED,
        payload: {
          roundIndex: roundIndex,
          userSpelling: userSpelling,
          correctSpelling: correctSpelling,
          pointValue: points,
        },
      });

      mixpanel.track("spelling_submitted", {
        game_id: gameData?.id,
        level_type: RoundType.TED_WORD,
        user_spelling: userSpelling,
        correct_spelling: correctSpelling,
      });
    }
  }

  if (roundState.isOnIntro) {
    return (
      <RoundIntroScreen
        roundType={RoundType.TED_WORD}
        roundIndex={roundIndex}
        onContinueClick={() =>
          roundDispatch({ type: TedWordAction.INTRO_CLOSED })
        }
      />
    );
  }

  return (
    <div
      className={cn(
        "w-full h-full",
        "flex flex-col items-center lg:gap-1",
        className,
      )}
    >
      <Header className="mb-1.5">{ROUND_TITLE_BY_ROUND_TYPE[RoundType.TED_WORD]}</Header>
      <h2 // VIDEO TITLE
        className={cn(
          "text-center mb-1 font-semibold",
          windowHeight === WindowSize.SMALL ? "text-xs" : "text-md",
          "lg:text-md",
        )}
      >
        {videoTitle}
      </h2>
      <div // VIDEO AREA
        className={cn(
          "lg:max-h-dvh",
          windowHeight === WindowSize.LARGE
            ? "min-h-50 max-h-[75%]"
            : "min-h-30 max-h-[30%]",
          "mb-1.5",
        )}
      >
        {!hideClipLengthButtons && (
          <div // CLIP LENGTH
            className={cn(
              "m-0 p-px",
              "w-full",
              "flex justify-evenly gap-5",
              "border border-b-0",
            )}
          >
            <ClipLengthButton
              selected={!jumpToWord}
              onClick={() => setJumpToWord(false)}
            >
              Full Clip
            </ClipLengthButton>
            <ClipLengthButton
              selected={jumpToWord}
              onClick={() => setJumpToWord(true)}
            >
              Jump to Word
            </ClipLengthButton>
          </div>
        )}
        <VideoEmbed
          key={jumpToWord.toString()}
          className={cn(
            "lg:h-70 h-[90%] mb-1 aspect-video",
            windowHeight === WindowSize.LARGE ? "max-h-70" : "max-h-40",
          )}
          slug={videoSlug}
          muted={false}
          startTime={jumpToWord ? videoWordTime : videoStartTime}
          endTime={videoEndTime}
        />
      </div>
      <Caption
        wordToSpell={word.wordText}
        sentence={sentence}
        spelled={roundComplete}
        correct={roundState.answeredCorrectly}
        onClick={() => {
          if (jumpToWord) {
            setJumpToWord(false);
            setTimeout(() => setJumpToWord(true), 0);
          } else {
            setJumpToWord(true);
          }
        }}
        className={cn(
          "flex-1",
          // "flex basis-7 grow mb-2",
          "min-h-7 max-h-15",
          // windowHeight === WindowSize.LARGE ? "max-h-21" : "max-h-15",
        )}
      />
      <div // SCORE AREA
        className={cn(
          "uppercase text-sm",
          "w-full",
          "mb-1",
          "flex justify-between",
        )}
      >
        <p>
          Attempts:{" "}
          {TED_WORD_SETTINGS.maxAttempts - roundState.userSpellings.length}/
          {TED_WORD_SETTINGS.maxAttempts}
        </p>
        <p>Reward: {getWordScore(roundState.numWrongAnswers)}</p>
      </div>

      <WordSpellingRecord
        numTotalWords={3}
        completedSpellings={roundState.userSpellings}
        hideCorrectSpelling={true}
        hideScore={true}
        showCorrectness={true}
        className={cn("grow basis-0 min-h-15 max-h-30", "lg:flex-2", "mb-2")}
        suppressCurrent={roundComplete}
      />

      {roundComplete ? (
        <div // GAME END AREA
          className={cn(
            "flex-1 flex flex-col justify-start items-center gap-4",
          )}
        >
          <NextRoundButton
            className="mt-2"
            onClick={() => {
              roundDispatch({ type: TedWordAction.ROUND_COMPLETED });
              onComplete();
            }}
            allowEnter={true}
          />
        </div>
      ) : (
        <div
          className={cn(
            "w-full",
            "flex-1 flex flex-col",
            inputType === "touch" ? "justify-end" : "justify-start",
          )}
        >
          <KeyboardGroup
            onSubmit={handleKeyboardGroupSubmit}
            heightShrinkCutoff={725}
          />
        </div>
      )}
    </div>
  );
}

export default function WrappedTedWordRound(props: TedWordRoundProps) {
  return (
    <TedWordProvider key={props.roundIndex} roundIndex={props.roundIndex}>
      <TedWordRound {...props} />
    </TedWordProvider>
  );
}

function getWordScore(numWrongAnswers: number): number {
  return (
    TED_WORD_SETTINGS.maxScore -
    TED_WORD_SETTINGS.scoreSubtracted * numWrongAnswers
  );
}
